import { Box, Grid } from '@mui/material'
import FormControlRadio, { radioButtonChoiceOptions } from 'components/FormControlRadio'
import FormInput from 'components/FormInput'
import { FormInputSelect, ItemOption } from 'components/FormInputSelect'
import React, { useCallback, useEffect, useState } from 'react'
import { Control, UseFormSetValue, UseFormWatch } from 'react-hook-form'
import addressService from 'services/address.service'
import genericService from 'services/generic.service'
import { HearingIdentificationType } from 'types/hearing-identification.type'
import { useHearingIdentificationBlockFieldsContext } from '../../../../../../contexts/hearing-identification-block-fields.context'

type InstitutionallyWelcomedGroupProps = {
  control: Control<HearingIdentificationType>
  watch: UseFormWatch<HearingIdentificationType>
  setValue: UseFormSetValue<HearingIdentificationType>
}

const WELCOMED_REASON_OTHER_OPTION_ID = '10'
const RJ_STATE_OPTION_ID = '33'

export const InstitutionallyWelcomedGroup: React.FC<InstitutionallyWelcomedGroupProps> = ({
  control,
  watch,
  setValue,
}) => {
  const { isBlockedFields } = useHearingIdentificationBlockFieldsContext()

  const [welcomedReasonOptions, setWelcomedReasonOptions] = useState<ItemOption[]>([])
  const [rjCitiesOptions, setRjCitiesOptions] = useState<ItemOption[]>([])
  const [institutionsOptions, setInstitutionsOptions] = useState<ItemOption[]>([])
  const [statesOptions, setStatesOptions] = useState<ItemOption[]>([])

  const hasWelcomed = watch('identificacaoAdolescente.acolhidoInsticionalmente')

  const welcomedReasonIdValue = watch('identificacaoAdolescente.idMotivoAcolhidoInstitucionalmente')
  const hasWelcomedInRioState = watch('identificacaoAdolescente.acolhidoInsticionalmenteEstadoRJ')
  const cidadeIdWatch = watch(
    'identificacaoAdolescente.idAcolhidoInsticionalmenteMunicipioEstadoRJ',
  )

  const isWelcomedReason =
    welcomedReasonIdValue && String(welcomedReasonIdValue) === WELCOMED_REASON_OTHER_OPTION_ID

  const fillInputSelects = useCallback(async (): Promise<void> => {
    try {
      const [resWelcomedReasonOptions, resRjCitiesOptions, resStatesOptions] = await Promise.all([
        genericService.get('/motivo-acolhido-inst').then((res) => res.data),
        addressService.getAllCitiesByStateId(RJ_STATE_OPTION_ID).then((res) => res.data),
        addressService.getAllFullNameStates().then((res) => res.data),
      ])

      setWelcomedReasonOptions(resWelcomedReasonOptions)
      setRjCitiesOptions(resRjCitiesOptions)

      const newResStatesOptions = resStatesOptions.filter((item) => item.value !== '33')
      setStatesOptions(newResStatesOptions)
    } catch (err) {
      console.error('Input select Requests: ', err)
    }
  }, [setWelcomedReasonOptions])

  useEffect(() => {
    fillInputSelects()
  }, [fillInputSelects])

  useEffect(() => {
    ;(async () => {
      if (cidadeIdWatch && cidadeIdWatch !== '0') {
        const resInstitutionsOptions = await genericService
          .get(`/servico-acolhimento/${cidadeIdWatch}`)
          .then((res) => res.data)
          .catch(() => console.log(''))
        if (resInstitutionsOptions) {
          setInstitutionsOptions(resInstitutionsOptions)
        }
      }
    })()
  }, [cidadeIdWatch])

  useEffect(() => {
    if (hasWelcomed === 'N') {
      setValue('identificacaoAdolescente.idMotivoAcolhidoInstitucionalmente', '0')
      setValue('identificacaoAdolescente.descricaoMotivoAcolhidoInstitucionalmente', '')
      setValue('identificacaoAdolescente.acolhidoInsticionalmenteEstadoRJ', '')
      setValue('identificacaoAdolescente.idAcolhidoInsticionalmenteMunicipioEstadoRJ', '0')
      setValue('identificacaoAdolescente.idAcolhidoInsticionalmenteServicoAcolhimento', '0')
      setValue('identificacaoAdolescente.idAcolhidoInsticionalmenteOutroEstado', '0')
    }
  }, [hasWelcomed, setValue])

  useEffect(() => {
    if (hasWelcomedInRioState === 'N') {
      setValue('identificacaoAdolescente.idAcolhidoInsticionalmenteMunicipioEstadoRJ', '0')
      setValue('identificacaoAdolescente.idAcolhidoInsticionalmenteServicoAcolhimento', '0')
    } else if (hasWelcomedInRioState === 'S') {
      setValue('identificacaoAdolescente.idAcolhidoInsticionalmenteOutroEstado', '0')
    }
  }, [hasWelcomedInRioState, setValue])

  return (
    <>
      <Grid item lg={hasWelcomed === 'S' ? 3 : 12} xs={12}>
        <FormControlRadio
          control={control}
          name='identificacaoAdolescente.acolhidoInsticionalmente'
          label='Já foi acolhido institucionalmente?'
          options={radioButtonChoiceOptions}
          disabled={isBlockedFields}
        />
      </Grid>
      {hasWelcomed === 'S' && (
        <>
          <Grid item lg={3} xs={12}>
            <FormInputSelect
              control={control}
              name='identificacaoAdolescente.idMotivoAcolhidoInstitucionalmente'
              label='Qual o motivo?'
              options={welcomedReasonOptions}
            />
          </Grid>

          {isWelcomedReason && (
            <Grid item lg={6} xs={12}>
              <FormInput
                control={control}
                name='identificacaoAdolescente.descricaoMotivoAcolhidoInstitucionalmente'
                label='Qual?'
                placeholder='Informe outro motivo'
                maxLength={60}
              />
            </Grid>
          )}

          <Grid item lg={3} xs={12}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: {
                  md: 'start',
                  lg: isWelcomedReason ? 'start' : 'center',
                },
              }}
            >
              <FormControlRadio
                control={control}
                name='identificacaoAdolescente.acolhidoInsticionalmenteEstadoRJ'
                label='No Rio de Janeiro?'
                options={radioButtonChoiceOptions}
              />
            </Box>
          </Grid>

          {hasWelcomedInRioState === 'S' && (
            <>
              <Grid item lg={3} xs={12}>
                <FormInputSelect
                  control={control}
                  name='identificacaoAdolescente.idAcolhidoInsticionalmenteMunicipioEstadoRJ'
                  label='Município'
                  options={rjCitiesOptions}
                />
              </Grid>

              {cidadeIdWatch && cidadeIdWatch !== '' && cidadeIdWatch !== '0' && (
                <Grid item lg={6} xs={12}>
                  <FormInputSelect
                    control={control}
                    name='identificacaoAdolescente.idAcolhidoInsticionalmenteServicoAcolhimento'
                    label='Instituição'
                    options={institutionsOptions}
                  />
                </Grid>
              )}
            </>
          )}
          {hasWelcomedInRioState === 'N' && (
            <Grid item lg={3} xs={12}>
              <FormInputSelect
                control={control}
                name='identificacaoAdolescente.idAcolhidoInsticionalmenteOutroEstado'
                label='Estado'
                options={statesOptions}
              />
            </Grid>
          )}
        </>
      )}
    </>
  )
}
